import type { AllConsentNames, ConsentManagerOptions } from "@c15t/nextjs"

/**
 * Absolute URL so the consent dialog's Privacy Policy link works from both
 * `www.webscraping.app` (renders the policy) and `console.webscraping.app`
 * (does not host `/privacy`).
 */
export const PRIVACY_POLICY_URL = "https://www.webscraping.app/privacy#cookies"

export const CONSENT_STORAGE_KEY = "wsapp_consent"

/**
 * Cookie domain used when running on a `*.webscraping.app` host.
 * Empty string in all other contexts (local dev, preview deploys) so the
 * browser scopes the cookie to the current host only.
 */
export const CONSENT_COOKIE_DOMAIN = ".webscraping.app"

export const CONSENT_CATEGORIES: readonly AllConsentNames[] = [
  "necessary",
  "functionality",
  "measurement",
  "marketing",
] as const

export const CONSENT_TRANSLATIONS = {
  en: {
    cookieBanner: {
      title: "We value your privacy",
      description:
        "We use cookies to keep you signed in and, with your permission, to understand how you use our product. You can accept all, reject non-essential, or pick categories.",
      acceptAll: "Accept all",
      rejectAll: "Reject non-essential",
      customize: "Manage preferences",
    },
    consentManagerDialog: {
      title: "Cookie preferences",
      description:
        "Choose which categories you want to allow. You can change this any time from the footer.",
      save: "Save preferences",
      acceptAll: "Accept all",
      rejectAll: "Reject non-essential",
    },
    consentTypes: {
      necessary: {
        title: "Strictly necessary",
        description:
          "Session authentication, CSRF tokens, and preferences needed for the Service to function. Always on.",
      },
      measurement: {
        title: "Analytics",
        description:
          "PostHog product analytics and session replay (with inputs masked). Helps us improve the product.",
      },
      marketing: {
        title: "Marketing",
        description: "Reserved for future marketing tools. Currently not used.",
      },
      functionality: {
        title: "Functionality",
        description: "Optional enhancements. Currently not used.",
      },
      experience: {
        title: "Experience",
        description: "Optional personalisation. Currently not used.",
      },
    },
  },
} as const

export function buildConsentManagerOptions(
  overrides: Partial<ConsentManagerOptions> = {},
): ConsentManagerOptions {
  const defaultDomain = pickCookieDomain()
  const base: ConsentManagerOptions = {
    mode: "offline",
    consentCategories: [...CONSENT_CATEGORIES],
    storageConfig: {
      storageKey: CONSENT_STORAGE_KEY,
      defaultDomain,
      defaultExpiryDays: 180,
    },
    store: {
      legalLinks: {
        privacyPolicy: {
          href: PRIVACY_POLICY_URL,
          target: "_blank",
          rel: "noopener noreferrer",
        },
      },
    },
    translations: {
      translations: CONSENT_TRANSLATIONS,
      defaultLanguage: "en",
    },
  }
  // Cast needed because ConsentManagerOptions is a discriminated union on
  // `mode`; spreading Partial<…> widens the literal and TS cannot re-narrow.
  return { ...base, ...overrides } as ConsentManagerOptions
}

function pickCookieDomain(): string {
  if (globalThis.window === undefined) return ""
  const host = globalThis.window.location.hostname
  return host === "webscraping.app" || host.endsWith(".webscraping.app")
    ? CONSENT_COOKIE_DOMAIN
    : ""
}
